import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    ContactPage as InfoIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintInformationSheetPage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>('blank');
    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}-${currentYear + 1}`;

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '—';
    const dob = selectedStudent?.date_of_birth || '—';
    const classLabel = studentGroup || '—';
    const address = selectedStudent?.address || '—';
    const email = selectedStudent?.email || '—';
    const bloodGroup = selectedStudent?.blood_group || '—';
    const allergies = selectedStudent?.has_allergies
        ? (selectedStudent.allergy_details || (language === 'ar' ? 'نعم' : 'Oui'))
        : (language === 'ar' ? 'لا يوجد' : language === 'en' ? 'None' : 'Aucune');
    const chronic = selectedStudent?.has_health_issues
        ? (selectedStudent.health_other_detail || (language === 'ar' ? 'نعم' : 'Oui'))
        : (language === 'ar' ? 'لا يوجد' : language === 'en' ? 'None' : 'Aucune');

    // Parse authorized pickup lines
    const authorizedPickupList = useMemo(() => {
        if (!selectedStudent?.authorized_pickup) return ['', '', ''];
        const lines = selectedStudent.authorized_pickup.split('\n').filter(l => l.trim().length > 0);
        return [lines[0] || '', lines[1] || '', lines[2] || ''];
    }, [selectedStudent]);

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الحقل' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Student Full Name' : "Nom et prénom de l'apprenant", studentFullName],
            [language === 'ar' ? 'تاريخ الميلاد' : language === 'en' ? 'Birth Date' : 'Date de naissance', dob],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe demandée', classLabel],
            [language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse', address],
            [language === 'ar' ? 'البريد الإلكتروني' : language === 'en' ? 'Email' : 'E-mail', email],
            [language === 'ar' ? 'الزمرة الدموية' : language === 'en' ? 'Blood Group' : 'Groupe sanguin', bloodGroup],
            [language === 'ar' ? 'الحساسية' : language === 'en' ? 'Allergies' : 'Allergies', allergies],
            [language === 'ar' ? 'مرض مزمن' : language === 'en' ? 'Chronic Illness' : 'Maladie chronique', chronic]
        ];
        exportToExcel(`Fiche_Renseignements_${selectedStudent?.code || 'Vierge'}`, 'Renseignements', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'استمارة معلومات' : language === 'en' ? 'Information Sheet' : 'Fiche de renseignements';
        exportToWord(`Fiche_Renseignements_${selectedStudent?.code || 'Vierge'}`, title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '6px 8px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={8} md={6}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار' : language === 'en' ? 'Select' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— استمارة فارغة —' : language === 'en' ? '— Blank Form —' : '— Fiche vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'استمارة معلومات (فارغة)' : language === 'en' ? 'Information Sheet' : 'Fiche de renseignements'}
            icon={<InfoIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 820, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'استمارة معلومات' : language === 'en' ? 'INFORMATION SHEET' : 'FICHE DE RENSEIGNEMENTS'}
                    subtitle={`${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic Year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <Box sx={{ my: 2 }}>
                    {/* Top Identity Section with Photo Frame */}
                    <div style={{ display: 'flex', gap: 16, marginBottom: 18 }}>
                        <table style={{ flex: 1, borderCollapse: 'collapse', fontSize: 12 }}>
                            <tbody>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'اللقب والاسم للتلميذ' : language === 'en' ? 'Student Full Name' : "Nom et prénom de l'apprenant"}
                                    </td>
                                    <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'تاريخ ومكان الميلاد' : language === 'en' ? 'Date & Place of birth' : 'Date et lieu de naissance'}
                                    </td>
                                    <td style={cellStyle()}>{dob}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'القسم المطلوب' : language === 'en' ? 'Requested Class' : 'Classe demandée'}
                                    </td>
                                    <td style={cellStyle()}>{classLabel}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'المدرسة السابقة' : language === 'en' ? 'Previous School' : 'École précédente'}
                                    </td>
                                    <td style={cellStyle()}>—</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'الأب — الاسم، المهنة، الهاتف' : language === 'en' ? 'Father — Name, Job, Phone' : 'Père — nom, profession, téléphone'}
                                    </td>
                                    <td style={cellStyle()}>{selectedStudent?.guardian_first_name ? `${selectedStudent.guardian_first_name} ${selectedStudent.guardian_last_name || ''} · ${selectedStudent.guardian_phone || ''}` : '—'}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'الأم — الاسم، المهنة، الهاتف' : language === 'en' ? 'Mother — Name, Job, Phone' : 'Mère — nom, profession, téléphone'}
                                    </td>
                                    <td style={cellStyle()}>—</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'العنوان' : language === 'en' ? 'Address' : 'Adresse'}
                                    </td>
                                    <td style={cellStyle()}>{address}</td>
                                </tr>
                                <tr>
                                    <td style={cellStyle(200, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                        {language === 'ar' ? 'البريد الإلكتروني' : language === 'en' ? 'Email' : 'E-mail'}
                                    </td>
                                    <td style={cellStyle()}>{email}</td>
                                </tr>
                            </tbody>
                        </table>

                        {/* Photo Box */}
                        <div style={{
                            width: 120,
                            border: '1px dashed #777',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#fafafa',
                            padding: 8,
                            flexShrink: 0
                        }}>
                            {selectedStudent?.photo_base64 ? (
                                <img
                                    src={selectedStudent.photo_base64}
                                    alt="Student"
                                    style={{ width: '100%', maxHeight: 150, objectFit: 'cover' }}
                                />
                            ) : (
                                <span style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
                                    {language === 'ar' ? 'صورة' : language === 'en' ? 'Photo' : 'Photo'}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Middle Two-Column Section: Santé & Personnes autorisées */}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20 }}>
                        <tbody>
                            <tr>
                                {/* Left column: Santé */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineEnd: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th colSpan={2} style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'الصحة' : language === 'en' ? 'Health' : 'Santé'}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الحساسية' : language === 'en' ? 'Allergies' : 'Allergies'}</td>
                                                <td style={cellStyle()}>{allergies}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'مرض مزمن' : language === 'en' ? 'Chronic Illness' : 'Maladie chronique'}</td>
                                                <td style={cellStyle()}>{chronic}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الطبيب المعالج' : language === 'en' ? 'Treating Physician' : 'Médecin traitant'}</td>
                                                <td style={cellStyle()}>—</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(130)}>{language === 'ar' ? 'الزمرة الدموية' : language === 'en' ? 'Blood Group' : 'Groupe sanguin'}</td>
                                                <td style={cellStyle()}>{bloodGroup}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>

                                {/* Right column: Personnes autorisées à récupérer l'enfant */}
                                <td style={{ width: '50%', verticalAlign: 'top', border: 'none', paddingInlineStart: 10 }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                                        <thead>
                                            <tr style={{ backgroundColor: '#f2f2f2' }}>
                                                <th colSpan={2} style={{ ...cellStyle(), fontWeight: 'bold', textTransform: 'uppercase' }}>
                                                    {language === 'ar' ? 'المرخّص لهم باستلام الطفل' : language === 'en' ? 'Authorized Pickup Persons' : "Personnes autorisées à récupérer l'enfant"}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td style={cellStyle(35, { textAlign: 'center' })}>1.</td>
                                                <td style={cellStyle()}>{authorizedPickupList[0] || '—'}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(35, { textAlign: 'center' })}>2.</td>
                                                <td style={cellStyle()}>{authorizedPickupList[1] || '—'}</td>
                                            </tr>
                                            <tr>
                                                <td style={cellStyle(35, { textAlign: 'center' })}>3.</td>
                                                <td style={cellStyle()}>{authorizedPickupList[2] || '—'}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Signatures */}
                    <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                    <strong>{language === 'ar' ? 'الولي :' : language === 'en' ? 'Parent / Guardian :' : 'Le parent :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%' }} />
                                </td>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                    <strong>{language === 'ar' ? 'المدير (الختم) :' : language === 'en' ? 'Director (Stamp) :' : 'Le directeur (cachet) :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '70%', marginInlineStart: 'auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 30, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintInformationSheetPage;
